import { CommonModule } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AddressPayload } from "src/app/models/addressPayload";
import { BagService } from "src/app/core/services/bag.service";
import { SpinnerService } from "src/app/core/services/spinner.service";
import { AddressTypeMap } from "src/app/core/const/addressType.const";
import { TranslateModule } from "@ngx-translate/core";
import { Router } from "@angular/router";

declare var google: any;

@Component({
  selector: "megastore-change-address",
  templateUrl: "./change-address.component.html",
  styleUrls: ["./change-address.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class ChangeAddressComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('mapElement') mapElement!: ElementRef;
  
  changeAddressForm: FormGroup = new FormGroup({
    address: new FormControl("", [Validators.required]),
  });
  addresses: AddressPayload[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  addressTypeMap: { [key: number]: string } = AddressTypeMap;
  
  // Location functionality
  isGettingLocation: boolean = false;
  showMapModal: boolean = false;
  selectedLocationAddress: string = '';
  selectedLatLng: { lat: number; lng: number } | null = null;
  mapSelectedAddress: string = '';
  map: any;
  marker: any;
  isLocationSelected: boolean = false; // Track if location-based selection is active

  constructor(
    private dialogRef: MatDialogRef<ChangeAddressComponent>,
    private _bagService: BagService,
    private _spinnerService: SpinnerService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA)
    public data?: {
      addresses?: AddressPayload[];
      selectedAddress?: AddressPayload;
      access?: 'selectOnly' | 'editDelete';
    }
  ) {}

  ngOnInit(): void {
    // If addresses are passed from parent, use them; otherwise fetch from API
    if (this.data?.addresses && this.data.addresses.length > 0) {
      this.addresses = this.data.addresses;

      // Set default selected address from parent data
      if (this.data.selectedAddress && this.data.selectedAddress._id) {
        this.changeAddressForm.patchValue({
          address: this.data.selectedAddress._id,
        });
      } else {
        const defaultAddress = this.addresses.find(
          (addr) => addr.defaultAddress
        );
        if (defaultAddress && defaultAddress._id) {
          this.changeAddressForm.patchValue({
            address: defaultAddress._id,
          });
        }
      }
    } else {
      this.getAddress();
    }
    
    // Load saved location from localStorage if exists
    this.loadSavedLocation();
  }

  ngAfterViewInit(): void {
    // Map will be initialized when modal is opened
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  getAddressType(addressType: number): string {
    return this.addressTypeMap[addressType] || "Unknown Address";
  }

  getAddress(): void {
    this._spinnerService.addToLoader("getAddresses");
    this._bagService
      .getAddresslistData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AddressPayload[]) => {
          this._spinnerService.removeFromLoader("getAddresses");
          this.addresses = response;
          if (this.addresses.length > 0) {
            const defaultAddress = this.addresses.find(
              (addr) => addr.defaultAddress
            );
            if (defaultAddress && defaultAddress._id) {
              this.changeAddressForm.patchValue({
                address: defaultAddress._id,
              });
            }
          }
        },
        error: () => {
          this._spinnerService.removeFromLoader("getAddresses");
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  changeAddress(): void {
    // If location is selected, use that instead
    if (this.isLocationSelected && this.selectedLatLng) {
      this.dialogRef.close({
        action: "change-locally",
        address: this.selectedLocationAddress,
        latitude: this.selectedLatLng.lat,
        longitude: this.selectedLatLng.lng,
      });
      return;
    }
    
    const selectedAddressId = this.changeAddressForm.value.address;
    const selectedAddress = this.addresses.find(
      (addr) => addr._id === selectedAddressId
    );
    
    // Reset location selection when saved address is chosen
    if (selectedAddress) {
      this.isLocationSelected = false;
      localStorage.setItem('lastSelectionType', 'savedAddress');
    }
    
    this.dialogRef.close({
      action: "change",
      address: selectedAddress,
    });
  }

  editAddress(address: any): void {
    this.dialogRef.close({
      action: "edit",
      address: address,
    });
  }

  deleteAddress(address: any): void {
    this.dialogRef.close({
      action: "delete",
      address: address,
    });
  }

  addNewAddress(): void {
    this.router.navigateByUrl('/my-addresses/v2');
    this.dialogRef.close(null);
  }

  // Location Methods
  loadSavedLocation(): void {
    const savedLocation = localStorage.getItem('selectedLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        this.selectedLocationAddress = location.address;
        this.selectedLatLng = location.latLng;
        // Check if this was the last selected option
        const lastSelectionType = localStorage.getItem('lastSelectionType');
        if (lastSelectionType === 'location') {
          this.isLocationSelected = true;
          this.changeAddressForm.patchValue({ address: '' });
        }
      } catch (error) {
        console.error('Error loading saved location:', error);
      }
    }
  }

  getCurrentLocation(): void {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    this.isGettingLocation = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.selectedLatLng = { lat, lng };
        console.log("selectedLatLng", this.selectedLatLng);
        // Reverse geocode to get address
        this.reverseGeocode(lat, lng);
      },
      (error) => {
        this.isGettingLocation = false;
        console.error('Error getting location:', error);
        alert('Unable to get your location. Please check your browser settings.');
      }
    );
  }

  reverseGeocode(lat: number, lng: number): void {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat, lng };

    geocoder.geocode({ location: latlng }, (results: any, status: any) => {
      this.isGettingLocation = false;
      if (status === 'OK' && results[0]) {
        this.selectedLocationAddress = results[0].formatted_address;
        this.saveLocationToStorage();
      } else {
        console.error('Geocoder failed:', status);
        this.selectedLocationAddress = `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        this.saveLocationToStorage();
      }
    });
  }

  openMapSelection(): void {
    this.showMapModal = true;
    // Wait for DOM to update before initializing map
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  closeMapModal(): void {
    this.showMapModal = false;
    this.mapSelectedAddress = '';
  }

  initializeMap(): void {
    if (!this.mapElement) return;

    // Default center (use saved location or default coordinates)
    const defaultCenter = this.selectedLatLng || { lat: 32.5656, lng: 13.1897 }; // Libya default

    this.map = new google.maps.Map(this.mapElement.nativeElement, {
      center: defaultCenter,
      zoom: 15,
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Update marker and address when map is dragged
    this.map.addListener('center_changed', () => {
      const center = this.map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      this.getAddressFromLatLng(lat, lng);
    });

    // Get address when dragging ends
    this.map.addListener('idle', () => {
      const center = this.map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      this.getAddressFromLatLng(lat, lng);
    });

    // Try to get current location and center map
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.map.setCenter(pos);
          this.getAddressFromLatLng(pos.lat, pos.lng);
        },
        () => {
          // If geolocation fails, use default or saved location
          this.getAddressFromLatLng(defaultCenter.lat, defaultCenter.lng);
        }
      );
    }
  }

  getAddressFromLatLng(lat: number, lng: number): void {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat, lng };
    geocoder.geocode({ location: latlng }, (results: any, status: any) => {
      if (status === 'OK' && results[0]) {
        this.mapSelectedAddress = results[0].formatted_address;
      } else {
        this.mapSelectedAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    });
  }

  confirmMapLocation(): void {
    if (!this.map) return;

    const center = this.map.getCenter();
    const lat = center.lat();
    const lng = center.lng();

    this.selectedLatLng = { lat, lng };
    this.selectedLocationAddress = this.mapSelectedAddress;
    this.saveLocationToStorage();
    
    // Reset saved address selection and highlight location selection
    this.isLocationSelected = true;
    this.changeAddressForm.patchValue({ address: '' });
    localStorage.setItem('lastSelectionType', 'location');
    
    this.closeMapModal();
  }

  saveLocationToStorage(): void {
    if (this.selectedLatLng && this.selectedLocationAddress) {
      const locationData = {
        address: this.selectedLocationAddress,
        latLng: this.selectedLatLng,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('selectedLocation', JSON.stringify(locationData));

      // Reset saved address selection and highlight location selection
      this.isLocationSelected = true;
      this.changeAddressForm.patchValue({ address: '' });
      localStorage.setItem('lastSelectionType', 'location');
    }
  }

  // Handle when a saved address is selected
  onSavedAddressSelect(): void {
    if (this.changeAddressForm.value.address) {
      this.isLocationSelected = false;
      localStorage.setItem('lastSelectionType', 'savedAddress');
    }
  }
}
